/**
 * Piece sets. Indexed by PieceType order: King, Advisor, Elephant, Horse, Rook,
 * Cannon, Pawn.
 *
 * - `trad`    traditional Chinese characters, with the distinct Red/Black forms
 *             used on a real board (帥/將, 俥/車, 傌/馬, 炮/砲, 兵/卒).
 * - `simp`    simplified Chinese.
 * - `western` WXF-standard Latin letters, for players who don't read Chinese.
 */

export type PieceStyle = "trad" | "simp" | "western" | "symbol";

const KEY = "xq-pieces";

const SETS: Record<PieceStyle, { red: string[]; black: string[]; scale: number; sans: boolean }> = {
  trad: {
    red: ["帥", "仕", "相", "傌", "俥", "炮", "兵"],
    black: ["將", "士", "象", "馬", "車", "砲", "卒"],
    scale: 1,
    sans: false,
  },
  simp: {
    red: ["帅", "仕", "相", "马", "车", "炮", "兵"],
    black: ["将", "士", "象", "马", "车", "炮", "卒"],
    scale: 1,
    sans: false,
  },
  western: {
    red: ["K", "A", "E", "H", "R", "C", "P"],
    black: ["K", "A", "E", "H", "R", "C", "P"],
    scale: 0.86,
    sans: true,
  },
  // Graphical symbols — clearly non-Chinese, one distinct icon per piece type.
  // Chariot and Horse borrow the universally-recognised chess rook/knight; the
  // rest use distinct geometric marks. Colour (red/black) tells the sides apart.
  symbol: {
    red: ["♚", "✚", "◆", "♞", "♜", "◉", "▲"],
    black: ["♚", "✚", "◆", "♞", "♜", "◉", "▲"],
    scale: 1.0,
    sans: true,
  },
};

let style: PieceStyle = ((): PieceStyle => {
  // Guarded: storage can throw outright (a third-party iframe with storage
  // blocked, some private windows), and this runs as the module loads.
  let s: string | null = null;
  try { s = localStorage.getItem(KEY); } catch { /* storage unavailable */ }
  return s === "trad" || s === "simp" || s === "western" || s === "symbol" ? s : "trad";
})();

export function getPieceStyle(): PieceStyle {
  return style;
}
export function setPieceStyle(s: PieceStyle) {
  style = s;
  localStorage.setItem(KEY, s);
}
export function pieceSet() {
  return SETS[style];
}
